import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { gql } from '@soundxyz/gql-string';
import { Filters } from '@components/Explore/Filters';
import { MobileFilters } from '@components/Explore/MobileFilters';
import { CollectionCard } from '@components/SongCards/CollectionCard';
import { GridSkeleton } from '@components/basics/GridSkeleton';
import { useAuth } from '@context/auth/base';
import { useParams } from '@context/params';
import type { FragmentType, ProfileCollectionPageQueryVariables } from '@graphql/reactQuery';
import {
  CollectionSectionUserInfoFragmentDoc,
  getFragment,
  ProfileCollectionPageDocument,
  ReleaseAlbumRevealFilterOption,
  ReleasesCollectorSortEnum,
  SortOrder,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { getSortOrder } from '@utils/helpers';
import { EmptyMyCollection } from '../MyCollection/EmptyMyCollection';
import { CardsContainer } from './CardsContainer';
import { CollectionSort } from './CollectionSort';
import { SectionHeader } from './SectionHeader';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ProfileCollectionPage(
    $userId: UUID!
    $after: String
    $filter: UserCollectedReleasesFilter
    $sort: ReleasesCollectorSortEnum
    $sortOrder: SortOrder
  ) {
    user(id: $userId) {
      id
      publicAddress
      collectedReleases(
        pagination: { first: 20, after: $after, sort: $sortOrder }
        filter: $filter
        sort: $sort
      ) {
        edges {
          node {
            id
            vipNftsCount
            gaNftsCount
            ownedSerialNumbers
            release {
              id
              ...CollectionCard
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  fragment CollectionSectionUserInfo on User {
    id
    webappUri
  }
`);

export const CollectionSection = ({
  user,
}: {
  user: FragmentType<CollectionSectionUserInfoFragmentDoc>;
}) => {
  const { authUser } = useAuth();

  const { id: userId, webappUri } = getFragment(CollectionSectionUserInfoFragmentDoc, user);

  const { genreKey, first_nft_collected_date } = useParams();

  const firstNftCollectedAtSort = getSortOrder({ sortType: first_nft_collected_date });

  const filter = {
    genres: genreKey ? [genreKey] : null,
  } satisfies ProfileCollectionPageQueryVariables['filter'];

  const {
    orderedList: collectedReleases,
    isLoadingNewPage: loading,
    hasNextPage = false,
    loadMoreNextPage: loadMore,
  } = useInfiniteQuery(ProfileCollectionPageDocument, {
    getNextPageParam({ data: { user } }) {
      return (
        user?.collectedReleases.pageInfo.hasNextPage && {
          after: user.collectedReleases.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {
      userId,
      filter,
      firstNftCollectedAtSort,
    },
    variables({ pageParam }) {
      return {
        userId,
        after: pageParam?.after || null,
        sortOrder: firstNftCollectedAtSort === 'ASC' ? SortOrder.Asc : SortOrder.Desc,
        filter: {
          releaseAlbumRevealStatus: ReleaseAlbumRevealFilterOption.OnlyNotRevealedAlbums,
          ...filter,
        },
        sort: !!firstNftCollectedAtSort
          ? ReleasesCollectorSortEnum.FirstNftCollectedDate
          : ReleasesCollectorSortEnum.NftsCount,
      };
    },
    list({ user }) {
      return user?.collectedReleases.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  const isOwner = authUser?.id === userId;

  const [bottomRef, isBottom] = useInView({
    threshold: 0.1,
  });

  useEffect(() => {
    if (!isBottom || !hasNextPage) return;

    loadMore();
  }, [isBottom, hasNextPage, loadMore]);

  if (!loading && collectedReleases.length === 0) {
    return (
      <>
        <SectionHeader header="Collection" userWebappUri={webappUri} />
        <div className="mt-4 hidden px-4 md:flex md:justify-between lt:px-0">
          <Filters showAvailabilityFilter={false} showGenreFilter showLocationFilter={false} />
          <CollectionSort />
        </div>
        <div className="mt-2 flex justify-between px-4 md:hidden">
          <MobileFilters
            showAvailabilityFilter={false}
            showGenreFilter
            showLocationFilter={false}
          />
          <CollectionSort />
        </div>
        <CardsContainer fullWidth hasFilter>
          <EmptyMyCollection isOwner={isOwner} />
        </CardsContainer>
      </>
    );
  }

  return (
    <>
      <SectionHeader header="Collection" userWebappUri={webappUri} />
      <div className="mt-4 hidden px-4 md:flex md:justify-between lt:px-0">
        <Filters showAvailabilityFilter={false} showGenreFilter showLocationFilter={false} />
        <CollectionSort />
      </div>
      <div className="flex justify-between px-4 md:hidden">
        <MobileFilters showAvailabilityFilter={false} showGenreFilter showLocationFilter={false} />
        <CollectionSort />
      </div>
      {loading ? (
        <div className="mt-3 px-4 md:mt-6 lt:px-0">
          <GridSkeleton />
        </div>
      ) : (
        <CardsContainer fullWidth={false} hasFilter>
          {collectedReleases.map(collectedRelease => (
            <CollectionCard
              userId={userId}
              key={collectedRelease.id}
              data={collectedRelease.release}
              ownedSerialNumbers={collectedRelease.ownedSerialNumbers}
              vipNftsCount={collectedRelease.vipNftsCount}
              gaNftsCount={collectedRelease.gaNftsCount}
              playlist={{
                type: 'HOLDER',
                associationId: userId,
              }}
            />
          ))}
          <div ref={bottomRef} />
        </CardsContainer>
      )}
    </>
  );
};
